/* assets/vc-collection.js — the arch card's light and the collection grid (task-10-brief.md; spec §8.5).
 *
 * 1. Candlelight and the second look, for every .vc-card on the page. The listeners are delegated from the document,
 *    so cards that the infinite scroll appends, or that another section renders, need no wiring.
 *    - Ported from prototypes/product.html:363-395, candle(): a time-based eased follow of the pointer, covering the
 *      fraction EASE of the way per 60 fps frame whatever the frame rate, plus a slow, low flicker on the flame's
 *      opacity. It runs on requestAnimationFrame only while a card is lit or the light is still travelling.
 *    - It sets --x / --y on the niche, and the card's CSS draws the light (snippets/vc-product-card.liquid).
 *    - Pointer hover toggles .is-lit, which also cross-dissolves to the second look. Touch is left out: a tap follows
 *      the link. Keyboard focus lights the card through :focus-visible in CSS, and starts the flicker here.
 *    - Reduced motion: the light jumps to the pointer and does not flicker. The CSS transitions are 1ms.
 * 2. <vc-collection-grid>: the reveal of .vc-reveal items (assets/vc-theme.css), and infinite scroll.
 *    - The grid claims its items (data-vc-watched) before DOMContentLoaded; layout/theme.liquid shows any item left
 *      unclaimed then, so a module that failed never leaves the grid hidden (Task 10 fix round).
 *    - Ported from D:/mrs-studios-shopify/theme/assets/mrs-collection.js:26-98, initInfiniteScroll(). An
 *      IntersectionObserver on #vc-scroll-sentinel (rootMargin 600px) fetches its data-next-url with
 *      &sections=<section id>.
 *    - The Section Rendering API answers {id: html}, and honours `page`
 *      (https://shopify.dev/docs/api/ajax/section-rendering). The next page's items move into the grid, and the
 *      sentinel takes that page's next url.
 *    - preview_theme_id is carried onto the request, as mrs-collection.js:5-12 does, so a theme preview keeps
 *      rendering the previewed theme's section.
 *    - A failed fetch brings back the "More pieces" link (real pagination), so the rest of the collection stays
 *      reachable.
 *    - connectedCallback runs again when the theme editor re-renders the section:
 *      https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements
 */
const EASE = 0.085;                 // product.html:369: 8.5% of the remaining way per 60 fps frame
const Y_MAX = 0.8;                  // product.html:377: the light never sinks below 80% of the niche
const REST = { x: 0.5, y: 0.34 };   // product.html:366: where the candle waits before the pointer comes
const SCROLL_MARGIN = '600px 0px';  // mrs-collection.js:92: start loading 600px before the sentinel shows
const STAGGER_MS = 90;              // items that come into view together arrive one after another

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------------------------------------------------------------- 1. candlelight */
const lamps = new WeakMap();

function lampOf(card) {
  let st = lamps.get(card);
  if (!st) {
    const niche = card.querySelector('.vc-card__niche');
    if (!niche) return null;
    st = { card, niche, flame: card.querySelector('.vc-card__flame'), x: REST.x, y: REST.y, tx: REST.x, ty: REST.y, on: false, raf: 0, last: 0 };
    lamps.set(card, st);
  }
  return st;
}

function tick(st, t) {
  const reduce = reducedMotion();
  const dt = st.last ? Math.min(64, t - st.last) : 16.67;
  st.last = t;
  const k = reduce ? 1 : 1 - Math.pow(1 - EASE, dt / 16.67);
  st.x += (st.tx - st.x) * k;
  st.y += (st.ty - st.y) * k;
  st.niche.style.setProperty('--x', `${(st.x * 100).toFixed(2)}%`);
  st.niche.style.setProperty('--y', `${(st.y * 100).toFixed(2)}%`);
  if (st.flame) {
    const f = 0.5 + 0.5 * Math.sin(t / 530) * Math.sin(t / 1270 + 1.3); // product.html:374
    st.flame.style.opacity = reduce ? '' : (0.92 + 0.08 * f).toFixed(3);
  }
  const travelling = Math.abs(st.tx - st.x) > 0.002 || Math.abs(st.ty - st.y) > 0.002;
  if ((st.on && !reduce) || travelling) st.raf = requestAnimationFrame((n) => tick(st, n));
  else { st.raf = 0; st.last = 0; }
}

function kick(st) {
  if (!st.raf) { st.last = 0; st.raf = requestAnimationFrame((t) => tick(st, t)); }
}

function aim(st, e) {
  const r = st.niche.getBoundingClientRect();
  if (!r.width || !r.height) return;
  st.tx = (e.clientX - r.left) / r.width;
  st.ty = Math.min(Y_MAX, (e.clientY - r.top) / r.height);
}

function light(st, on) {
  st.on = on;
  st.card.classList.toggle('is-lit', on);
  if (on) kick(st);
}

const cardOf = (node) => (node instanceof Element ? node.closest('.vc-card') : null);

document.addEventListener('pointerover', (e) => {
  if (e.pointerType === 'touch') return;
  const card = cardOf(e.target);
  if (!card || card.contains(e.relatedTarget)) return;
  const st = lampOf(card);
  if (!st) return;
  aim(st, e);
  light(st, true);
});
document.addEventListener('pointerout', (e) => {
  if (e.pointerType === 'touch') return;
  const card = cardOf(e.target);
  if (!card || card.contains(e.relatedTarget)) return;
  const st = lamps.get(card);
  if (st && !card.matches(':focus-visible')) light(st, false);
});
document.addEventListener('pointermove', (e) => {
  if (e.pointerType === 'touch') return;
  const card = cardOf(e.target);
  const st = card && lamps.get(card);
  if (!st || !st.on) return;
  aim(st, e);
  kick(st);
}, { passive: true });
document.addEventListener('focusin', (e) => {
  const card = cardOf(e.target);
  if (!card || !card.matches(':focus-visible')) return;
  const st = lampOf(card);
  if (st) light(st, true);
});
document.addEventListener('focusout', (e) => {
  const card = cardOf(e.target);
  const st = card && lamps.get(card);
  if (st && !card.matches(':hover')) light(st, false);
});

/* ---------------------------------------------------------------- 2. the grid */
/** The Section Rendering request for the next page: `next` (paginate.next.url) with sections=<id>, keeping a
 *  theme preview's preview_theme_id (mrs-collection.js:5-12). */
export function sectionsUrl(next, id, pageSearch = window.location.search) {
  const url = new URL(next, window.location.href);
  url.searchParams.set('sections', id);
  const preview = new URLSearchParams(pageSearch).get('preview_theme_id');
  if (preview && !url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', preview);
  return url.pathname + url.search;
}

export class VcCollectionGrid extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-vc-grid-list]');
    this.status = this.querySelector('[data-vc-grid-status]');
    this.more = this.querySelector('[data-vc-grid-more]');
    this.sentinel = this.querySelector('#vc-scroll-sentinel');
    this.busy = false;
    this.reveal = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => this.onReveal(entries), { rootMargin: '0px 0px -6% 0px' })
      : null;
    this.watch(this);
    this.initScroll();
  }

  disconnectedCallback() {
    if (this.reveal) this.reveal.disconnect();
    if (this.io) this.io.disconnect();
  }

  /** Claim the .vc-reveal items (data-vc-watched: layout/theme.liquid's DOMContentLoaded fallback shows only unclaimed
   *  ones) and reveal each as it comes into view; without IntersectionObserver, show them at once. */
  watch(root) {
    for (const el of root.querySelectorAll('.vc-reveal:not(.is-in)')) {
      el.dataset.vcWatched = '';
      if (this.reveal) this.reveal.observe(el);
      else el.classList.add('is-in');
    }
  }

  onReveal(entries) {
    let n = 0;
    for (const en of entries) {
      if (!en.isIntersecting) continue;
      if (!reducedMotion()) en.target.style.transitionDelay = `${Math.min(n, 5) * STAGGER_MS}ms`;
      n += 1;
      en.target.classList.add('is-in');
      this.reveal.unobserve(en.target);
    }
  }

  initScroll() {
    if (!this.list || !this.sentinel || !this.dataset.sectionId || !('IntersectionObserver' in window)) return;
    if (!this.sentinel.dataset.nextUrl) return;
    if (this.more) this.more.hidden = true; // infinite scroll takes over; the link comes back if a fetch fails
    this.io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) this.load();
    }, { rootMargin: SCROLL_MARGIN });
    this.io.observe(this.sentinel);
  }

  say(text) {
    if (this.status) this.status.textContent = text;
  }

  async load() {
    const next = this.sentinel.dataset.nextUrl || '';
    if (this.busy || !next) return;
    this.busy = true;
    this.list.setAttribute('aria-busy', 'true');
    this.say('Loading more pieces');
    const id = this.dataset.sectionId;
    try {
      // No Accept header: Shopify answers a collection URL that accepts application/json alone with a 404 (it has no JSON format), and
      // `?sections=` is a section read, not a JSON format (CLAUDE.md, "A `?sections=` read must not send `Accept: application/json` alone").
      const res = await fetch(sectionsUrl(next, id));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = (await res.json())[id];
      if (!html) throw new Error(`section ${id} missing from the response`);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-vc-grid-list]');
      if (fresh) this.list.append(...fresh.children);
      this.watch(this.list);
      const after = doc.querySelector('#vc-scroll-sentinel');
      const nextNext = (after && after.dataset.nextUrl) || '';
      this.sentinel.dataset.nextUrl = nextNext;
      if (this.more) {
        if (nextNext) this.more.href = nextNext;
        else this.more.remove();
      }
      const total = Number(this.dataset.total) || 0;
      this.say(total ? `${this.list.children.length} of ${total} pieces` : '');
      if (nextNext) { // observe afresh: a tall screen may still see the sentinel (mrs-collection.js:74-75)
        this.io.unobserve(this.sentinel);
        this.io.observe(this.sentinel);
      } else {
        this.io.disconnect();
      }
    } catch (err) {
      console.warn('[vc-collection] infinite scroll:', err);
      this.say('');
      this.io.disconnect();
      if (this.more) this.more.hidden = false;
    } finally {
      this.busy = false;
      this.list.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('vc-collection-grid')) customElements.define('vc-collection-grid', VcCollectionGrid);
